import { useState, useMemo, useCallback } from "react";
import { format } from "date-fns";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { 
  Trash2, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  ClipboardList,
  GraduationCap,
  BookOpen,
  Calendar,
  Plus,
  Search,
  X,
  RefreshCw
} from "lucide-react";
import { DeleteAssignmentDialog } from "./DeleteAssignmentDialog";
import { useHasRole } from "@/hooks/useUserRoles";
import { cn } from "@/lib/utils";
import type { Assignment, AssignmentStatus, AssignmentType } from "@/types/assignments";

interface AssignmentListProps {
  assignments: Assignment[];
  isLoading: boolean;
  onRefresh?: () => void;
  onCreateNew?: () => void;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
  error?: Error | null;
}

type SortField = "user_name" | "type" | "item_name" | "assigned_by_name" | "assigned_at" | "due_date" | "status";
type SortDirection = "asc" | "desc";

const ITEMS_PER_PAGE = 20;

export function AssignmentList({ 
  assignments, 
  isLoading, 
  onRefresh,
  onCreateNew,
  onClearFilters,
  hasActiveFilters = false,
  error
}: AssignmentListProps) {
  const isAdmin = useHasRole("admin");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<Assignment | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>("assigned_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Memoized sort function
  const sortedAssignments = useMemo(() => {
    return [...assignments].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case "user_name":
          comparison = a.user_name.localeCompare(b.user_name);
          break;
        case "type":
          comparison = a.type.localeCompare(b.type);
          break;
        case "item_name":
          comparison = a.item_name.localeCompare(b.item_name);
          break;
        case "assigned_by_name":
          comparison = a.assigned_by_name.localeCompare(b.assigned_by_name);
          break;
        case "assigned_at":
          comparison = new Date(a.assigned_at).getTime() - new Date(b.assigned_at).getTime();
          break;
        case "due_date":
          const aDate = a.due_date ? new Date(a.due_date).getTime() : 0;
          const bDate = b.due_date ? new Date(b.due_date).getTime() : 0;
          comparison = aDate - bDate;
          break;
        case "status":
          const statusOrder = { not_started: 0, in_progress: 1, completed: 2 };
          comparison = statusOrder[a.status] - statusOrder[b.status];
          break;
      }
      
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [assignments, sortField, sortDirection]);

  // Paginate
  const totalPages = Math.ceil(sortedAssignments.length / ITEMS_PER_PAGE);
  const paginatedAssignments = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedAssignments.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedAssignments, currentPage]);

  // Handle sort
  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  }, [sortField]);

  const getSortIcon = useCallback((field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === "asc" 
      ? <ArrowUp className="h-4 w-4 ml-1" /> 
      : <ArrowDown className="h-4 w-4 ml-1" />;
  }, [sortField, sortDirection]);

  const getStatusBadge = useCallback((status: AssignmentStatus) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800 hover:bg-green-100">
            Completed
          </Badge>
        );
      case "in_progress":
        return (
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800 hover:bg-orange-100">
            In Progress
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700 hover:bg-gray-100">
            Not Started
          </Badge>
        );
    }
  }, []);

  const getTypeBadge = useCallback((type: AssignmentType) => {
    if (type === "curriculum") {
      return (
        <Badge className="bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800 hover:bg-blue-100">
          <GraduationCap className="h-3 w-3 mr-1" />
          Curriculum
        </Badge>
      );
    }
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800 hover:bg-green-100">
        <BookOpen className="h-3 w-3 mr-1" />
        Course
      </Badge>
    );
  }, []);

  const formatDate = useCallback((dateString: string | undefined) => {
    if (!dateString) return "—";
    return format(new Date(dateString), "MMM d, yyyy");
  }, []);

  const getInitials = useCallback((name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  }, []);

  const handleDeleteClick = useCallback((assignment: Assignment) => {
    setAssignmentToDelete(assignment);
    setDeleteDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback((open: boolean) => {
    setDeleteDialogOpen(open);
    if (!open) setAssignmentToDelete(null);
  }, []);

  // Error state
  if (error) {
    return (
      <div className="text-center py-12 border rounded-lg bg-destructive/5 animate-fade-in">
        <div className="h-12 w-12 mx-auto rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <X className="h-6 w-6 text-destructive" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Failed to load assignments</h3>
        <p className="text-muted-foreground mb-4 max-w-sm mx-auto">
          {error.message || "There was an error loading the assignments. Please try again."}
        </p>
        {onRefresh && (
          <Button onClick={onRefresh} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        )}
      </div>
    );
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Loading assignments">
        {/* Desktop skeleton */}
        <div className="hidden lg:block">
          <div className="border rounded-lg overflow-hidden">
            <div className="p-4 border-b bg-muted/30">
              <div className="flex gap-4">
                {[...Array(7)].map((_, i) => (
                  <Skeleton key={i} className="h-4 flex-1" />
                ))}
              </div>
            </div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-4 border-b last:border-b-0">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-4 w-32 flex-1" />
                  <Skeleton className="h-4 w-24 hidden xl:block" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-8 w-8" />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Tablet skeleton */}
        <div className="hidden md:block lg:hidden">
          <div className="border rounded-lg overflow-hidden">
            <div className="p-4 border-b bg-muted/30">
              <div className="flex gap-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-4 flex-1" />
                ))}
              </div>
            </div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-4 border-b last:border-b-0">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-8 w-8" />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Mobile skeleton */}
        <div className="md:hidden space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="p-4 border rounded-lg space-y-3 animate-pulse">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-8 w-8" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-20" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state - no assignments at all
  if (assignments.length === 0 && !hasActiveFilters) {
    return (
      <div className="text-center py-12 border rounded-lg bg-muted/20 animate-fade-in">
        <div className="h-16 w-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
          <ClipboardList className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No assignments yet</h3>
        <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
          Create your first assignment to start tracking learning progress for users.
        </p>
        {onCreateNew && (
          <Button onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Create Assignment
          </Button>
        )}
      </div>
    );
  }

  // Empty state - no results from filters
  if (assignments.length === 0 && hasActiveFilters) {
    return (
      <div className="text-center py-12 border rounded-lg bg-muted/20 animate-fade-in">
        <div className="h-16 w-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
          <Search className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No assignments match your filters</h3>
        <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
          Try adjusting your search criteria or clear the filters to see all assignments.
        </p>
        {onClearFilters && (
          <Button onClick={onClearFilters} variant="outline">
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Desktop Table View (>1024px) */}
      <div className="hidden lg:block border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead 
                className="cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("user_name")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("user_name")}
                aria-label="Sort by user name"
              >
                <div className="flex items-center">
                  User
                  {getSortIcon("user_name")}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("type")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("type")}
                aria-label="Sort by type"
              >
                <div className="flex items-center">
                  Type
                  {getSortIcon("type")}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("item_name")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("item_name")}
                aria-label="Sort by item name"
              >
                <div className="flex items-center">
                  Item Name
                  {getSortIcon("item_name")}
                </div>
              </TableHead>
              <TableHead 
                className="hidden xl:table-cell cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("assigned_by_name")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("assigned_by_name")}
                aria-label="Sort by assigned by"
              >
                <div className="flex items-center">
                  Assigned By
                  {getSortIcon("assigned_by_name")}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("assigned_at")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("assigned_at")}
                aria-label="Sort by assigned date"
              >
                <div className="flex items-center">
                  Assigned
                  {getSortIcon("assigned_at")}
                </div>
              </TableHead>
              <TableHead 
                className="hidden xl:table-cell cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("due_date")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("due_date")}
                aria-label="Sort by due date"
              >
                <div className="flex items-center">
                  Due Date
                  {getSortIcon("due_date")}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("status")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("status")}
                aria-label="Sort by status"
              >
                <div className="flex items-center">
                  Status
                  {getSortIcon("status")}
                </div>
              </TableHead>
              <TableHead className="w-[60px]">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedAssignments.map((assignment, index) => (
              <TableRow 
                key={assignment.id} 
                className={cn(
                  "hover:bg-muted/30 transition-colors",
                  index === 0 && "animate-fade-in"
                )}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <UserAvatar
                      avatarPath={assignment.user_avatar}
                      fullName={assignment.user_name}
                      className="h-8 w-8"
                      thumbnailSize={64}
                    />
                    <span className="font-medium truncate max-w-[150px]">
                      {assignment.user_name}
                    </span>
                  </div>
                </TableCell>
                <TableCell>{getTypeBadge(assignment.type)}</TableCell>
                <TableCell>
                  <span className="truncate max-w-[200px] block" title={assignment.item_name}>
                    {assignment.item_name}
                  </span>
                </TableCell>
                <TableCell className="hidden xl:table-cell text-muted-foreground">
                  {assignment.assigned_by_name}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(assignment.assigned_at)}
                </TableCell>
                <TableCell className="hidden xl:table-cell">
                  {assignment.due_date ? (
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {formatDate(assignment.due_date)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>{getStatusBadge(assignment.status)}</TableCell>
                <TableCell>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClick(assignment)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive transition-colors"
                      aria-label={`Delete assignment for ${assignment.user_name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Tablet Table View (768-1024px) */}
      <div className="hidden md:block lg:hidden border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead 
                className="cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("user_name")}
              >
                <div className="flex items-center">
                  User
                  {getSortIcon("user_name")}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("item_name")}
              >
                <div className="flex items-center">
                  Item
                  {getSortIcon("item_name")}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("type")}
              >
                <div className="flex items-center">
                  Type
                  {getSortIcon("type")}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort("status")}
              >
                <div className="flex items-center">
                  Status
                  {getSortIcon("status")}
                </div>
              </TableHead>
              <TableHead className="w-[60px]">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedAssignments.map((assignment) => (
              <TableRow key={assignment.id} className="hover:bg-muted/30 transition-colors">
                <TableCell>
                  <div className="flex items-center gap-2">
                    <UserAvatar
                      avatarPath={assignment.user_avatar}
                      fullName={assignment.user_name}
                      className="h-8 w-8"
                      thumbnailSize={64}
                    />
                    <span className="font-medium truncate max-w-[120px]">
                      {assignment.user_name}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="truncate max-w-[150px] block" title={assignment.item_name}>
                    {assignment.item_name}
                  </span>
                </TableCell>
                <TableCell>{getTypeBadge(assignment.type)}</TableCell>
                <TableCell>{getStatusBadge(assignment.status)}</TableCell>
                <TableCell>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClick(assignment)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive transition-colors"
                      aria-label={`Delete assignment for ${assignment.user_name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View (<768px) */}
      <div className="md:hidden space-y-3">
        {paginatedAssignments.map((assignment, index) => (
          <div
            key={assignment.id}
            className={cn(
              "p-4 border rounded-lg bg-card hover:bg-muted/30 transition-all",
              index === 0 && "animate-fade-in"
            )}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <UserAvatar
                  avatarPath={assignment.user_avatar}
                  fullName={assignment.user_name}
                  className="h-10 w-10"
                  thumbnailSize={64}
                />
                <div className="min-w-0">
                  <p className="font-medium truncate">{assignment.user_name}</p>
                  <p className="text-sm text-muted-foreground truncate">
                    {assignment.item_name}
                  </p>
                </div>
              </div>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteClick(assignment)}
                  className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label={`Delete assignment for ${assignment.user_name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {getTypeBadge(assignment.type)}
              {getStatusBadge(assignment.status)}
              {assignment.required && (
                <Badge variant="destructive" className="text-xs">Required</Badge>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Assigned by</p>
                <p className="font-medium truncate">{assignment.assigned_by_name}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Date</p>
                <p className="font-medium">{formatDate(assignment.assigned_at)}</p>
              </div>
              {assignment.due_date && (
                <div className="col-span-2">
                  <p className="text-muted-foreground text-xs">Due date</p>
                  <p className="font-medium flex items-center gap-1 text-orange-600 dark:text-orange-400">
                    <Calendar className="h-3 w-3" />
                    {formatDate(assignment.due_date)}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination className="mt-6">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                className={cn(
                  "cursor-pointer transition-opacity",
                  currentPage === 1 && "pointer-events-none opacity-50"
                )}
                aria-disabled={currentPage === 1}
                aria-label="Go to previous page"
              />
            </PaginationItem>
            
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              // Show first, last, current, and adjacent pages
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => setCurrentPage(page)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                      aria-label={`Go to page ${page}`}
                      aria-current={currentPage === page ? "page" : undefined}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                );
              }
              // Show ellipsis
              if (page === currentPage - 2 || page === currentPage + 2) {
                return (
                  <PaginationItem key={page}>
                    <span className="px-2 text-muted-foreground">...</span>
                  </PaginationItem>
                );
              }
              return null;
            })}
            
            <PaginationItem>
              <PaginationNext
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                className={cn(
                  "cursor-pointer transition-opacity",
                  currentPage === totalPages && "pointer-events-none opacity-50"
                )}
                aria-disabled={currentPage === totalPages}
                aria-label="Go to next page"
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {/* Results info */}
      <div 
        className="text-sm text-muted-foreground text-center"
        role="status"
        aria-live="polite"
      >
        Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, assignments.length)} of {assignments.length} assignments
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteAssignmentDialog
        assignment={assignmentToDelete}
        open={deleteDialogOpen}
        onOpenChange={handleDialogClose}
        onSuccess={onRefresh}
      />
    </div>
  );
}
